import { IndexedDB } from 'services/indexed-db'
import peaksWorkerUrl from './audio-peaks.worker.ts?worker&url'
import {
  IWaveformIDBRecord,
  IWaveformPeaks,
  IWaveformRequest,
  IActiveJob,
  IAudioCtxWindow
} from './types'
import { getLimitsPerDevice } from './helpers'

export const CONFIG = {
  watchdogMs: 30_000, // 30s: kill jobs that hang
  dbName: 'audio-peaks-cache',
  table: 'peaks',
  expiration: 14 * 24 * 60 * 60 * 1000, // 14 days in ms: IDB cache TTL
  bars: { min: 500, max: 3000, perMin: 1000 }
} as const

// singleton pattern
// Caches and active-job tracking only work if everyone shares the same instance.
// If three components each created their own WaveformManager,
// they'd each have separate caches and would download/decode the same audio three times.
export class WaveformManager {
  private static instance: WaveformManager
  private memoryCache = new Map<string, IWaveformPeaks | null>()
  private activeJobs = new Map<string, IActiveJob>()
  private workerBlobUrlPromise: Promise<string> | null = null

  private idb = IndexedDB.getInstance(CONFIG.dbName, [
    {
      name: CONFIG.table,
      options: { keyPath: 'assetId' },
      expirationTime: CONFIG.expiration
    }
  ])

  private constructor() {}

  //this forces everyone to go through getInstance()
  //guaranteeing exactly one instance ever exists.
  // So the first call constructs it; every later call returns the same one.
  static getInstance(): WaveformManager {
    return (this.instance ??= new WaveformManager())
  }

  /**
   * Main entry point to request waveform data
   */
  async request(req: IWaveformRequest): Promise<IWaveformPeaks | null> {
    const { assetId, url, fileSize = 0, duration = 0 } = req
    const { durationLimit, sizeLimit } = getLimitsPerDevice()
    const supported = this.isSupported()

    if (!supported || fileSize > sizeLimit || duration > durationLimit) {
      return null
    }

    // 1. Check Memory & IDB Cache
    // memoryCache is for items already ahndleed during this instance, so no need to wait for idb request
    if (this.memoryCache.has(assetId)) {
      return this.memoryCache.get(assetId)!
    }

    // only after checking locao memory check in  idb
    const cached = await this.readFromIDB(assetId)
    if (cached) {
      this.memoryCache.set(assetId, cached)
      return cached
    }

    // 2. De-duplicate or Start New Job
    const existing = this.activeJobs.get(assetId)
    if (existing) {
      existing.refCount++
      return existing.promise
    }

    return this.startNewJob(assetId, url)
  }

  /**
   * Orchestrates the async lifecycle of a waveform generation
   */
  private startNewJob(
    assetId: string,
    url: string
  ): Promise<IWaveformPeaks | null> {
    const controller = new AbortController()
    const job: IActiveJob = {
      refCount: 1,
      controller,
      promise: (async () => {
        try {
          const result = await this.runExecutionPipeline(
            assetId,
            url,
            controller
          )
          this.finalizeJob(assetId, result)
          return result
        } catch {
          this.finalizeJob(assetId, null)
          return null
        }
      })()
    }

    job.timer = setTimeout(() => this.release(assetId, true), CONFIG.watchdogMs)
    this.activeJobs.set(assetId, job)

    return job.promise
  }

  private async runExecutionPipeline(
    assetId: string,
    url: string,
    controller: AbortController
  ): Promise<IWaveformPeaks> {
    // 1. Fetch
    const response = await fetch(url, { signal: controller.signal })
    const buffer = await response.arrayBuffer()

    // 2. Decode
    const audioCtx = this.getAudioContext()
    const audioBuffer = await audioCtx.decodeAudioData(buffer)

    // 3. Worker Processing
    const bars = this.calculateBarCount(audioBuffer.duration)
    const peaks = await this.spawnWorker(audioBuffer, bars, assetId)
    const result = { peaks, duration: audioBuffer.duration }
    void this.writeToIDB(assetId, result)

    return result
  }

  private async spawnWorker(
    audio: AudioBuffer,
    bars: number,
    assetId: string
  ): Promise<Float32Array> {
    // Dev: Vite serves the worker as ESM (imports must resolve), so load the URL
    // directly as a module worker — it's same-origin in dev anyway.
    // Prod: worker is bundled iife and served from the CDN; fetch+blob as classic
    // to avoid the cross-origin SecurityError.
    const worker = import.meta.env.DEV
      ? new Worker(peaksWorkerUrl, { type: 'module' })
      : new Worker(await this.getWorkerBlobUrl())
    const job = this.activeJobs.get(assetId)
    if (job) job.worker = worker

    return new Promise((resolve, reject) => {
      worker.onmessage = (e: MessageEvent) => {
        resolve(new Float32Array(e.data))
      }
      worker.onerror = (err: ErrorEvent) => {
        reject(err)
      }

      const channels = Array.from({ length: audio.numberOfChannels }, (_, i) =>
        audio.getChannelData(i).slice()
      )

      worker.postMessage(
        { channels, bars },
        channels.map(c => c.buffer)
      )
    })
  }

  /**
   * Called by components when they no longer need the waveform or if the watchdogMs have oassed
   */
  release(assetId: string, force = false): void {
    const job = this.activeJobs.get(assetId)
    if (!job) return

    job.refCount--
    // if there are multiple items waiting for the waveform and 30s have passed, we send force true to kill the job
    if (job.refCount <= 0 || force) {
      this.cleanupResources(job)
      this.activeJobs.delete(assetId)
    }
  }

  private finalizeJob(assetId: string, result: IWaveformPeaks | null) {
    const job = this.activeJobs.get(assetId)
    if (job) {
      this.cleanupResources(job)
      this.activeJobs.delete(assetId)
    }
    if (result) this.memoryCache.set(assetId, result)
  }

  private cleanupResources(job: IActiveJob) {
    if (job.timer) clearTimeout(job.timer)
    if (job.worker) job.worker.terminate()
    if (!job.controller.signal.aborted) job.controller.abort()
  }

  // --- Utility Helpers ---

  private isSupported(): boolean {
    const w = window as IAudioCtxWindow
    return (
      typeof Worker !== 'undefined' &&
      !!(w.OfflineAudioContext || w.webkitOfflineAudioContext)
    )
  }

  private getAudioContext(): OfflineAudioContext {
    const w = window as IAudioCtxWindow
    const Ctor = w.OfflineAudioContext || w.webkitOfflineAudioContext
    if (!Ctor) throw new Error('OfflineAudioContext not supported')
    return new Ctor(1, 1, 44100)
  }

  // Workers must be same-origin with the page. Our chunks are served from cdn.renderforest.com,
  // so `new Worker(cdnUrl)` throws SecurityError on stage/prod page origins. Fetching the script
  // and wrapping it in a blob URL produces a same-origin Worker. Requires CORS on the CDN.
  private getWorkerBlobUrl(): Promise<string> {
    return (this.workerBlobUrlPromise ??= fetch(peaksWorkerUrl)
      .then(r => r.blob())
      .then(b => URL.createObjectURL(b)))
  }

  private calculateBarCount(duration: number): number {
    const { min, max, perMin } = CONFIG.bars
    return Math.min(max, Math.max(min, Math.floor((duration / 60) * perMin)))
  }

  private async readFromIDB(assetId: string): Promise<IWaveformPeaks | null> {
    try {
      const record = await this.idb.readRecord<IWaveformIDBRecord>(
        CONFIG.table,
        assetId
      )
      return record
        ? { peaks: new Float32Array(record.peaks), duration: record.duration }
        : null
    } catch {
      return null
    }
  }

  private async writeToIDB(assetId: string, data: IWaveformPeaks) {
    this.idb
      .saveRecord<IWaveformIDBRecord>(CONFIG.table, { assetId, ...data })
      .catch(() => {})
  }
}
